import type { Pot, RoomView, SeatView, SeatViewState } from '@chipless/shared';
import { SEAT_COUNT } from '@chipless/shared';
import { Badge, Card, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { pt, ptWithUnit, seatLabel } from '../format.js';
import { BoardIndicator } from './BoardIndicator.js';
import classes from './SeatRing.module.css';

/**
 * 卓の配置で8席を表示する（要件11.1〜11.6）。
 *
 * 席0 を手前中央に置き、時計回りに並べる。中央にポット総額を最も大きく出す。
 */

const STATE_BADGE: Record<SeatViewState, { label: string; color: string } | null> = {
  EMPTY: null,
  ACTIVE: null,
  FOLDED: { label: 'フォールド', color: 'gray' },
  ALLIN: { label: 'オールイン', color: 'orange' },
  SITTING_OUT: { label: '離席', color: 'gray' },
  NEEDS_REBUY: { label: 'リバイ必要', color: 'red' },
};

/**
 * 席の位置。卓の中心を (50, 50) として配る。
 *
 * 10等分した楕円のうち12時の位置（最上部）を空白にし、残り9スロットに席0〜8を時計回りに
 * 割り当てる。12時は中央ディスプレイの正面にあたり、ここに席を置くとポット総額に被る。
 * 空白の1枠を設けることで、席カードの幅を維持したまま隣どうしが重ならない。
 *
 * 半径は、席カードの幅（10rem）の半分が卓の外へ出ないように決める。
 */
const SEAT_RADIUS_X = 37;
const SEAT_RADIUS_Y = 41;

/**
 * 10 等分の角度。スロット 0 が12時（真上）で時計回り。
 *
 * 席 N はスロット N+1 に入る。スロット 0 は空白。
 */
const SLOT_COUNT = SEAT_COUNT + 1;

function seatAngle(seatNo: number): number {
  // スロット 0 が12時（-π/2）。席N → スロット N+1。
  const slot = seatNo + 1;
  return -Math.PI / 2 + (slot / SLOT_COUNT) * Math.PI * 2;
}

function seatPosition(seatNo: number): { left: string; top: string } {
  const angle = seatAngle(seatNo);
  return {
    left: `${50 + Math.cos(angle) * SEAT_RADIUS_X}%`,
    top: `${50 + Math.sin(angle) * SEAT_RADIUS_Y}%`,
  };
}

/** ベット額のチップを、席と卓の中心の間に置く。 */
function betPosition(seatNo: number): { left: string; top: string } {
  const angle = seatAngle(seatNo);
  return {
    left: `${50 + Math.cos(angle) * 23}%`,
    top: `${50 + Math.sin(angle) * 25}%`,
  };
}

function SeatCard({ seat }: { seat: SeatView }): React.ReactElement {
  const badge = STATE_BADGE[seat.state];
  const classNames = [classes.seatCard];
  if (seat.isTurn) classNames.push(classes.turn);
  if (seat.state === 'FOLDED') classNames.push(classes.folded);
  if (!seat.occupied) classNames.push(classes.empty);

  return (
    <Card
      withBorder
      padding="xs"
      radius="md"
      className={classNames.join(' ')}
      data-testid={`seat-${seat.seatNo}`}
      data-state={seat.state}
      data-turn={seat.isTurn ? 'true' : 'false'}
      data-connected={seat.connected ? 'true' : 'false'}
    >
      {seat.isButton && (
        <ThemeIcon
          className={classes.button}
          radius="xl"
          size="md"
          color="gray"
          variant="filled"
          title="ディーラーボタン"
        >
          <Text fw={700} size="xs">
            D
          </Text>
        </ThemeIcon>
      )}

      <Stack gap={2}>
        <Group gap={6} justify="space-between" wrap="nowrap">
          <Text size="xs" c="dimmed">
            {seatLabel(seat.seatNo)}
          </Text>
          {seat.occupied && !seat.connected && (
            <Badge size="xs" color="red" variant="outline">
              切断
            </Badge>
          )}
        </Group>

        {seat.occupied ? (
          <>
            <Text fw={600} size="sm" truncate data-testid={`seat-${seat.seatNo}-name`}>
              {seat.name}
            </Text>
            <Text fw={700} size="lg" data-testid={`seat-${seat.seatNo}-stack`}>
              {pt(seat.stack)}
            </Text>
            {/* 進行中ハンドへのリバイは次のハンドから有効になる（要件3.3）。積まれている
                ことを見せないと、操作が効いていないように見えて二重に足される。 */}
            {seat.pendingBuyIn > 0 && (
              <Badge
                size="xs"
                color="teal"
                variant="light"
                data-testid={`seat-${seat.seatNo}-pending`}
              >
                次ハンド +{pt(seat.pendingBuyIn)}
              </Badge>
            )}
            {badge !== null && (
              <Badge size="xs" color={badge.color} variant="light">
                {badge.label}
              </Badge>
            )}
          </>
        ) : (
          <Text size="sm" c="dimmed">
            空席
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function PotBreakdown({ pots }: { pots: readonly Pot[] }): React.ReactElement | null {
  // サイドポットが無ければ内訳は出さない（要件11.6）。
  if (pots.length <= 1) return null;
  return (
    <Stack gap={2} mt="xs">
      {pots.map((pot, i) => (
        <Text key={i} size="sm" c="dimmed">
          {i === 0 ? 'メイン' : `サイド${i}`} {ptWithUnit(pot.amount)}（
          {pot.eligible.map((s) => seatLabel(s)).join('・')}）
        </Text>
      ))}
    </Stack>
  );
}

export function SeatRing({ view }: { view: RoomView }): React.ReactElement {
  const hand = view.hand;

  return (
    <div className={classes.table}>
      {view.seats.map((seat) => (
        <div key={seat.seatNo} className={classes.seat} style={seatPosition(seat.seatNo)}>
          <SeatCard seat={seat} />
        </div>
      ))}

      {view.seats
        .filter((seat) => seat.streetBet > 0)
        .map((seat) => (
          <div
            key={`bet-${seat.seatNo}`}
            className={classes.bet}
            style={betPosition(seat.seatNo)}
            data-testid={`seat-${seat.seatNo}-bet`}
          >
            <Badge size="lg" color="yellow" variant="filled" radius="sm">
              {pt(seat.streetBet)}
            </Badge>
          </div>
        ))}

      <div className={classes.center}>
        {/* 物理のボードは卓の中央、ポットの手前に並ぶ。画面でも同じ関係にする。 */}
        <BoardIndicator street={hand === null ? null : hand.street} />

        <div>
          <Text size="sm" c="dimmed" tt="uppercase" fw={600}>
            ポット
          </Text>
          {/*
            ポット総額を最も目立つ形で出す（要件11.5）。

            ハンドが無いときも 0 を出す。この欄は「いま卓の中央にいくらあるか」を示し、
            卓がある限りその値は定まる。「—」にすると値が取れていないように読める。
          */}
          <Text
            fw={900}
            lh={1}
            style={{ fontSize: 'calc(var(--mantine-font-size-xl) * 2.6)' }}
            data-testid="pot-total"
          >
            {pt(hand?.potTotal ?? 0)}
          </Text>
          {hand !== null && <PotBreakdown pots={hand.pots} />}
        </div>
      </div>
    </div>
  );
}
